import { SearchOutlined } from '@ant-design/icons';
import { Button, Card, message, Space, Tag } from 'antd';
import stockAccountApiCall from 'apps/investment/apiCalls/stock-accounts';
import apiStockAccountLogsCall from 'apps/investment/apiCalls/stock-account-logs/list';
import { identificationTypeLabels } from 'apps/investment/constants';
import {
  eSignContractStatusColors,
  eSignContractStatusLabels,
  mutualFundAccountStatusColors,
  mutualFundAccountStatusLabels,
  stockAccountSourceColors,
  stockAccountStatusColors,
} from 'apps/investment/constants/stock-accounts';
import { DataTable } from 'common/data-table';
import OnlineTooltip from 'common/online-tooltip';
import CashSVG from 'common/svg/cash-svg';
import BooleanIcons from 'common/ui/boolean-icons';
import ErrorMessage from 'common/ui/ErrorMessage';
import InfoCard from 'common/ui/info-card';
import { LabelTooltip } from 'common/ui/label-tooltip';
import { useFetchApiList, usePostApi } from 'hooks/api';
import { capitalize } from 'lodash';
import { IdCardIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';
import { columnsStockAccountLogs } from './config';
import StockAccountQRNavigate from './qr-navigate';

const ItemDetail = ({ item, onRefresh }: { item: any; onRefresh?: () => void }) => {
  const {
    data: fundProductFees,
    error: errorFundProductFees,
    load: loadStockAccountLogs,
  } = useFetchApiList(apiStockAccountLogsCall, { resourceName: 'data' });

  // sync persists synchronously via StockAccountService.Repo.Update before
  // returning, so a refetch shows the new contract immediately.
  const refreshOnSuccess = () => onRefresh?.();

  const { post: onSyncEsign, isLoading: isSyncingEsign } = usePostApi(
    stockAccountApiCall.syncEsign,
    { onSuccess: refreshOnSuccess }
  );

  // request-new only asks NHSV to create the contract (account/info); NHSV
  // creates it asynchronously, so the refetch shows the contract only once
  // it exists ("Sync econtract" pulls it in later).
  const { post: onRequestNewEsign, isLoading: isRequestingNewEsign } =
    usePostApi(stockAccountApiCall.requestNewEsign, {
      onSuccess: () => {
        message.success(
          'Đã gửi yêu cầu. NHSV thường tạo hợp đồng sau vài giây; nếu chưa thấy, bấm "Sync econtract" để cập nhật.'
        );
        refreshOnSuccess();
      },
    });

  // reinit deliberately does NOT refetch. ReinitEKYCEsignLink only issues
  // the request to NHSV and returns; its doc comment says the caller must
  // "poll GetEKYCEsignLink again later, not expect a link back". Refetching
  // immediately would re-render the old, dead contract and look like the
  // reinit had failed. The new link arrives via the CONTRACT_INIT_CREATED
  // webhook or the daily sync cron.
  const { post: onReinitEsign, isLoading: isReinitingEsign } = usePostApi(
    stockAccountApiCall.reinitEsign,
    {
      onSuccess: () => {
        message.success(
          'Đã gửi yêu cầu khởi tạo lại. Hợp đồng mới sẽ có khi NHSV tạo xong; nếu chưa thấy, bấm "Sync econtract" để cập nhật.'
        );
      },
    }
  );

  useEffect(() => {
    if (item.id) loadStockAccountLogs({ stock_account_id: item.id });
  }, [item.id]);

  return (
    <div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard
            label="Account Number"
            value={
              <Space>
                <div>{item.account_number}</div>
                <OnlineTooltip
                  isOnline={item.is_online}
                  lastLoginAt={item.last_login_at}
                />
              </Space>
            }
          />
          <InfoCard label="Stock account ID" value={item.stock_account_id} />
          {item.account_number && (
            <InfoCard
              label="NHSV Lookup"
              value={
                <Space size="small">
                  <Link
                    to={`/nhsv/account-detail?account_number=${item.account_number}`}
                  >
                    <Button size="small" icon={<SearchOutlined />}>
                      Account
                    </Button>
                  </Link>
                  <Link
                    to={`/nhsv/order-history?account_number=${item.account_number}`}
                  >
                    <Button size="small" icon={<SearchOutlined />}>
                      Orders
                    </Button>
                  </Link>
                </Space>
              }
            />
          )}

          <InfoCard
            label="Status Code"
            value={
              <Tag color={stockAccountStatusColors[item.status]}>
                {item.status_label
                  ? `${item.status_label} (${item.status})`
                  : item.status}
              </Tag>
            }
          />
          <InfoCard
            label="Source"
            value={
              <Tag color={stockAccountSourceColors[item.source]}>
                {capitalize(item.source)}
              </Tag>
            }
          />

          <InfoCard label="Security Manager" value={item.security_manager} />
          <InfoCard label="Broker Name" value={item.broker_name || '-'} />
          <InfoCard
            label="Authentication Method"
            value={item.authentication_method}
          />
          <InfoCard
            label="Tài khoản Stag"
            value={
              <Link to={`/investment/users/${item.user_id}`}>
                {item.user_id}
              </Link>
            }
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoCard
            label="Created At"
            value={formatDateTime(item.created_at) || '-'}
          />
          <InfoCard
            label="Approved At"
            value={formatDateTime(item.approved_at) || '-'}
          />
          <InfoCard
            label="Updated At"
            value={formatDateTime(item.updated_at) || '-'}
          />
          <InfoCard
            label="Last Login At"
            value={formatDateTime(item.last_login_at) || '-'}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <InfoCard
            label="Security Account Data Status"
            value={
              item.security_account_data_status ? (
                <Tag
                  color={
                    item.security_account_data_status === 'active'
                      ? 'green'
                      : 'orange'
                  }
                >
                  {capitalize(item.security_account_data_status)}
                </Tag>
              ) : (
                '-'
              )
            }
          />
          <InfoCard
            value={<BooleanIcons value={item.is_register_sync_data_socket} />}
            label="Register sync socket?"
          />

          <InfoCard
            value={<BooleanIcons value={item.enable_margin_trading_account} />}
            label="Margin trading account?"
          />
          <InfoCard
            value={<BooleanIcons value={item.enable_payment_in_advance} />}
            label="Payment in advance?"
          />
          <InfoCard
            value={<BooleanIcons value={item.is_upload_nhsv_profile} />}
            label="Submitted account info?"
          />
          <InfoCard
            value={<BooleanIcons value={item.is_upload_nhsv_ekyc} />}
            label="Submitted EKYC info?"
          />
          <InfoCard
            value={<BooleanIcons value={item.is_vsd_approved} />}
            label="VSD duyệt"
          />
          <InfoCard
            value={<BooleanIcons value={item.is_account_mapping} />}
            label="Payment Area Mapping?"
          />
          <InfoCard
            value={<BooleanIcons value={item.can_sync_nhsv_data} />}
            label={
              <LabelTooltip
                label="Can sync NHSV data?"
                tooltipContent="can_sync_nhsv_data — has account number and data status is active (or has logged in to NHSV). When No, Stag skips every NHSV sync: balance, portfolio, orders, dividends."
              />
            }
          />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center justify-between">
          <span className="flex items-center">
            <IdCardIcon className="mr-1" />
            Hợp đồng CK
          </span>
          <Space size="small">
            <Button
              size="small"
              loading={isSyncingEsign}
              onClick={() => onSyncEsign({ id: item.id })}
            >
              Sync econtract
            </Button>
            <Button
              size="small"
              loading={isRequestingNewEsign}
              onClick={() => onRequestNewEsign({ id: item.id })}
            >
              Request econtract
            </Button>
            <Button
              size="small"
              loading={isReinitingEsign}
              onClick={() => onReinitEsign({ id: item.id })}
            >
              Reinit econtract
            </Button>
          </Space>
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard value={item.contract_number} label="Số hợp đồng CK (NHSV)" />
          <InfoCard
            value={<BooleanIcons value={item.is_sign_contract} />}
            label="Khách đã ký hđ CK"
          />
          <InfoCard
            value={<BooleanIcons value={item.is_contract_approve} />}
            label="NHSV đã duyệt hđ CK"
          />
          <InfoCard
            label="Esign Contract Status"
            value={
              item.esign_contract_status ? (
                <Tag color={eSignContractStatusColors[item.esign_contract_status]}>
                  {eSignContractStatusLabels[item.esign_contract_status] ||
                    item.esign_contract_status}
                </Tag>
              ) : (
                '-'
              )
            }
          />
          <InfoCard
            label={
              <LabelTooltip
                label="Esign Envelope Status"
                tooltipContent="esign_envelope_status — raw envStatus from NHSV/FPT eContract: processing, completed, rejected, voided, overdue"
              />
            }
            value={item.esign_envelope_status || '-'}
          />
          <InfoCard
            label={
              <LabelTooltip
                label="Esign Recipient Status"
                tooltipContent="esign_recipient_status — raw recipientStatus from NHSV: the customer's signature (accepted = signed)"
              />
            }
            value={item.esign_recipient_status || '-'}
          />
          <InfoCard
            label="Esign Contract Number"
            value={item.esign_contract_number || '-'}
          />
          <InfoCard
            label="Esign Contract Name"
            value={item.esign_contract_name || '-'}
          />
          <InfoCard
            label="Esign Contract ID"
            value={item.esign_contract_id || '-'}
          />
          <InfoCard
            label="Stag ghi nhận khách ký lúc"
            value={
              item.esign_signed_at ? formatDateTime(item.esign_signed_at) : '-'
            }
          />
          <InfoCard
            label="Stag ghi nhận duyệt lúc"
            value={
              item.esign_approved_at
                ? formatDateTime(item.esign_approved_at)
                : '-'
            }
          />
          <InfoCard
            className="md:col-span-3"
            label="Esign Contract Link"
            value={
              item.esign_contract_link ? (
                <a
                  href={item.esign_contract_link}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all"
                >
                  {item.esign_contract_link}
                </a>
              ) : (
                '-'
              )
            }
          />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          <IdCardIcon className="mr-1" />
          Hợp đồng CCQ
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard
            value={<BooleanIcons value={item.is_esign_contract_approve} />}
            label="Đã ký hđ CCQ"
          />
          <InfoCard
            label="Mutual Fund Account Status"
            value={
              item.mutual_fund_account_status ? (
                <Tag
                  color={
                    mutualFundAccountStatusColors[
                      item.mutual_fund_account_status
                    ]
                  }
                >
                  {mutualFundAccountStatusLabels[
                    item.mutual_fund_account_status
                  ] || item.mutual_fund_account_status}
                </Tag>
              ) : (
                '-'
              )
            }
          />
          <InfoCard
            label="Mutual Fund Account Approved At"
            value={
              item.mutual_fund_account_approved_at
                ? formatDateTime(item.mutual_fund_account_approved_at)
                : '-'
            }
          />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          <IdCardIcon className="mr-1" />
          Identification
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Full Name" value={item.full_name} />
          <InfoCard label="Email" value={item.email} />
          <InfoCard label="Phone" value={item.phone} />
          <InfoCard
            label={
              <LabelTooltip
                label="Địa chỉ thường trú"
                tooltipContent="residential_address"
              />
            }
            value={item.residential_address}
          />
          <InfoCard
            label={
              <LabelTooltip label="Địa chỉ liên hệ" tooltipContent="address" />
            }
            value={item.address}
          />
          <InfoCard label="Date of Birth" value={item.date_of_birth} />
          <InfoCard
            label="Gender"
            value={capitalize(item.gender) || '-'}
          />
          <InfoCard label="Nationality" value={item.nationality} />
          <InfoCard label="ID Number" value={item.id_number} />
          <InfoCard
            label="ID Type"
            value={identificationTypeLabels[item.id_type] || item.id_type}
          />
          <InfoCard label="ID Issue Date" value={item.id_issue_date} />
          <InfoCard label="ID Expired Date" value={item.id_expired_date} />
          <InfoCard
            className="col-span-2"
            label="ID Issue Place"
            value={item.id_issue_place}
          />
          <InfoCard label="Tax Number" value={item.tax_number} />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          <CashSVG className="mr-1" />
          Bank info
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Bank Name" value={item.bank_name} />
          <InfoCard label="Bank ID" value={item.bank_id} />
          <InfoCard label="Bank Account Name" value={item.bank_account_name} />
          <InfoCard
            label="Bank Account Number"
            value={item.bank_account_number}
          />
          <InfoCard label="Bank Branch Name" value={item.bank_branch_name} />
          <InfoCard label="Bank Branch ID" value={item.bank_branch_id} />
        </div>
      </div>

      <Card size="small" title="Lịch sử" className="mt-4">
        {!!errorFundProductFees ? (
          <ErrorMessage errors={errorFundProductFees} />
        ) : (
          <DataTable
            noViewOptions
            columns={columnsStockAccountLogs()}
            data={fundProductFees || []}
          />
        )}
      </Card>

      <StockAccountQRNavigate
        stockAccountId={item.id}
        accountNumber={item.account_number}
      />
    </div>
  );
};

export default ItemDetail;
